import React from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/avatar";
import { Switch } from "@/shared/ui/switch";
import { CalendarIcon, PenLine, Crown, User } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/ui/calendar";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/shared/lib/utils";
import { useNavigate } from "react-router-dom";
import { Pagination } from "@/shared/widgets/pagination";
import { IUser } from "../services/profiles/types";
import { useTranslation } from "@/shared/i18n";

interface UsersTableImprovedProps {
  users: IUser[] | undefined;
  isLoading: boolean;
  onVipToggle: ({ id, vip, vipExpiresAt }: { id: string, vip: boolean, vipExpiresAt: string }) => void;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    pageSizeOptions?: number[];
    showPageSizeOptions?: boolean;
    showTotalItems?: boolean;
    syncWithQueryParams?: boolean;
  };
}

export const UsersTableImproved = ({
  users,
  isLoading,
  onVipToggle,
  pagination,
}: UsersTableImprovedProps) => {
  const { t } = useTranslation('profiles');
  const formatDate = (date: string | null) => {
    if (!date) return t('notDefined');
    return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
  };

  const getInitials = (email: string, name?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="bg-[#222222] border-white/10 animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-700 rounded-full"></div>
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const shouldRenderPagination = Boolean(pagination);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {users?.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            onVipToggle={onVipToggle}
            formatDate={formatDate}
            getInitials={getInitials}
          />
        ))}
      </div>

      {shouldRenderPagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          pageSizeOptions={pagination.pageSizeOptions ?? [10, 20, 50, 100]}
          showPageSizeOptions={pagination.showPageSizeOptions ?? true}
          showTotalItems={pagination.showTotalItems ?? true}
          setCurrentPage={pagination.onPageChange ?? (() => { })}
          onPageSizeChange={pagination.onPageSizeChange}
          syncWithQueryParams={pagination.syncWithQueryParams ?? false}
        />
      )}
    </div>
  );
};

interface UserCardProps {
  user: IUser;
  onVipToggle: ({ id, vip, vipExpiresAt }: { id: string, vip: boolean, vipExpiresAt: string }) => void;
  formatDate: (date: string | null) => string;
  getInitials: (email: string, name?: string) => string;
}

const UserCard = ({ user, onVipToggle, formatDate, getInitials }: UserCardProps) => {
  const { t } = useTranslation('profiles');
  const navigate = useNavigate();
  const name = user.name || user.username || user.creatorData?.name || user.creatorData?.username || t('user');
  const isVip = user.vip || false;
  const isSubscribed = user?.subscription?.active;
  const isCreator = Boolean(user.creatorData || (user as any).isCreator);
  
  // Lógica de prioridade para avatar: creatorData.avatar > avatar > fallback com iniciais
  const avatarUrl = user.creatorData?.avatar || user.avatar || null;

  return (
    <Card className={cn(
      "bg-[#222222] border-white/10 hover:border-white/20 transition-all duration-200 hover:shadow-lg",
      isVip && "ring-2 ring-yellow-500/20 border-yellow-500/30"
    )}>
      <CardContent className="p-4">
        {/* Header com Avatar e Status */}
        <div className="mb-4">
          <div className="flex items-start gap-3 mb-2">
            <Avatar className="w-12 h-12 flex-shrink-0">
              <AvatarImage src={avatarUrl || undefined} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold">
                {getInitials(user.email, name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-white text-sm truncate" title={name}>
                {name}
              </h3>
              <p className="text-xs text-gray-400 truncate" title={user.email}>
                {user.email}
              </p>
            </div>
          </div>
          
          {/* Badges de Status e Username */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Creator Badge */}
            {isCreator && (
              <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 text-xs">
                <User className="w-3 h-3 mr-1" />
                {t('creator')}
              </Badge>
            )}
            
            {/* VIP Badge */}
            {isVip && (
              <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 text-xs">
                <Crown className="w-3 h-3 mr-1" />
                {t('vip')}
              </Badge>
            )}
            
            {/* Username Badge */}
            {(user.creatorData?.username || user.username) && (
              <Badge 
                variant="outline" 
                className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 border-purple-500/30"
              >
                @{user.creatorData?.username || user.username}
              </Badge>
            )}
          </div>
        </div>

        {/* Informações Detalhadas */}
        <div className="mb-4 space-y-3">
          {/* Status da Assinatura */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">{t('subscription')}</span>
            <Badge 
              variant={isSubscribed ? "default" : "secondary"}
              className={cn(
                "text-xs",
                isSubscribed 
                  ? "bg-green-500/20 text-green-400 border-green-500/30" 
                  : "bg-red-500/20 text-red-400 border-red-500/30"
              )}
            >
              {isSubscribed ? t('active') : t('inactive')}
            </Badge>
          </div>
          
          {isSubscribed && user?.subscription?.expiresAt && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">{t('expiresAt')}</span>
              <span className="text-xs text-white">
                {formatDate(user.subscription.expiresAt)}
              </span>
            </div>
          )}

          {/* Data de Criação */}
          {(user as any).createdAt && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">{t('createdAt')}</span>
              <span className="text-xs text-white">
                {formatDate((user as any).createdAt)}
              </span>
            </div>
          )}

          {/* Última Atualização */}
          {(user as any).lastUpdatedAt && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">{t('lastUpdatedAt')}</span>
              <span className="text-xs text-white">
                {formatDate((user as any).lastUpdatedAt)}
              </span>
            </div>
          )}

          {/* ID do Usuário */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">ID</span>
            <span className="text-xs text-gray-300 font-mono truncate max-w-[100px]" title={user.id}>
              {user.id}
            </span>
          </div>

          {/* Firebase ID */}
          {(user as any).firebaseId && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Firebase ID</span>
              <span className="text-xs text-gray-300 font-mono truncate max-w-[100px]" title={(user as any).firebaseId}>
                {(user as any).firebaseId}
              </span>
            </div>
          )}

          {/* Stripe ID */}
          {(user as any).stripeId && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Stripe ID</span>
              <span className="text-xs text-gray-300 font-mono truncate max-w-[100px]" title={(user as any).stripeId}>
                {(user as any).stripeId}
              </span>
            </div>
          )}

          {/* Transações */}
          {user.transactions && user.transactions.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">{t('transactions')}</span>
              <Badge variant="outline" className="text-xs bg-green-500/20 text-green-400 border-green-500/30">
                {user.transactions.length}
              </Badge>
            </div>
          )}

          {/* Dados do Criador */}
          {user.creatorData && (
            <div className="pt-2 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">{t('creator')}</span>
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-xs",
                    (user.creatorData as any)?.isActive 
                      ? "bg-green-500/20 text-green-400 border-green-500/30"
                      : "bg-gray-500/20 text-gray-400 border-gray-500/30"
                  )}
                >
                  {(user.creatorData as any)?.isActive ? t('creatorActive') : t('creatorInactive')}
                </Badge>
              </div>
              
              {user.creatorData?.avatar && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-400">{t('avatar')}:</span>
                  <span className="text-xs text-gray-300 truncate max-w-[80px]" title={user.creatorData.avatar}>
                    {t('avatarDefined')}
                  </span>
                </div>
              )}
              
              {(user.creatorData as any)?.cover && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{t('cover')}:</span>
                  <span className="text-xs text-gray-300 truncate max-w-[80px]" title={(user.creatorData as any).cover}>
                    {t('coverDefined')}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Controles VIP */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">{t('vipLabel')}</span>
            <Switch
              checked={isVip}
              onCheckedChange={() => onVipToggle({
                id: user.id,
                vip: !isVip,
                vipExpiresAt: !isVip
                  ? new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString()
                  : user.vipExpiresAt
              })}
              className="data-[state=checked]:bg-yellow-500"
            />
          </div>

          {/* Data de Expiração VIP */}
          {isVip && (
            <div className="space-y-2">
              <span className="text-xs text-gray-400">{t('vipExpiration')}</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-between bg-[#1E1E1E] border-[#333333] text-white hover:bg-[#2A2A2A] text-xs"
                  >
                    {user.vipExpiresAt ? formatDate(user.vipExpiresAt) : t('selectDateForVip')}
                    <CalendarIcon className="ml-2 h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2 border-[#333333] bg-[#1E1E1E]" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={user.vipExpiresAt ? new Date(user.vipExpiresAt) : undefined}
                    onSelect={(date) => onVipToggle({
                      id: user.id,
                      vip: user.vip,
                      vipExpiresAt: date?.toISOString()
                    })}
                    initialFocus
                    className="rounded-md border-none bg-[#1E1E1E] text-white"
                    classNames={{
                      nav: "space-x-1 flex items-center justify-between px-2 py-2",
                      button_previous: "h-7 w-7 bg-transparent text-white hover:bg-[#333333] rounded-md absolute left-2",
                      button_next: "h-7 w-7 bg-transparent text-white hover:bg-[#333333] rounded-md absolute right-2",
                      weekday: "text-white/60 text-xs font-normal py-2",
                      day: "p-0 w-9 h-9 text-center text-sm",
                      day_button: "w-9 h-9 p-0 font-normal rounded-md hover:bg-[#333333] focus:outline-none focus:ring-2 focus:ring-yellow-500/50",
                      selected: "bg-yellow-500 text-white hover:bg-yellow-500 hover:text-white focus:bg-yellow-500 focus:text-white",
                      today: "border border-yellow-500 text-yellow-500 font-medium",
                      outside: "text-white/30",
                      disabled: "text-white/20 opacity-30",
                      month_caption: 'bg-red-500'
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {/* Botão de Editar */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <Button
            variant="outline"
            size="sm"
            className="w-full bg-[#1E1E1E] border-[#333333] text-white hover:bg-[#2A2A2A] hover:text-white"
            onClick={() =>
              navigate(`/users/${user.id}/edit`, {
                state: { user, from: window.location.pathname + window.location.search },
              })
            }
          >
            <PenLine className="w-4 h-4 mr-2" />
            {t('editUser')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
